import { forwardRef, useCallback, useState, type CSSProperties, type ReactNode } from "react";
import { LazyMotion, domAnimation } from "framer-motion";
import { FunnelAnnouncerContext, type FunnelAnnouncer } from "@funnel-ui/hooks/useFunnelAnnouncer";
import { DEFAULT_BRAND, type Brand } from "@funnel-ui/theme";
import { CharacterBackground } from "@funnel-ui/shell/CharacterBackground";

type FunnelShellProps = {
  brand?: Brand;
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  characterBackgroundUrl?: string;
  characterBackgroundColor?: string;
  className?: string;
  testId?: string;
};

const buildBrandStyle = (brand: Brand): CSSProperties => {
  const style: Record<string, string> = {};
  if (brand.primaryColor) style["--brand-primary"] = brand.primaryColor;
  if (brand.primaryForeground) style["--brand-primary-foreground"] = brand.primaryForeground;
  if (brand.backgroundColor) style["--brand-bg"] = brand.backgroundColor;
  if (brand.backgroundForeground) style["--brand-bg-foreground"] = brand.backgroundForeground;
  if (brand.radius) style["--brand-radius"] = brand.radius;
  if (brand.fontDisplay) style["--brand-font-display"] = brand.fontDisplay;
  if (brand.fontBody) style["--brand-font-body"] = brand.fontBody;
  return style;
};

export const FunnelShell = forwardRef<HTMLDivElement, FunnelShellProps>(function FunnelShell(
  {
    brand = DEFAULT_BRAND,
    header,
    footer,
    children,
    characterBackgroundUrl,
    characterBackgroundColor,
    className,
    testId,
  },
  ref,
) {
  const [announcement, setAnnouncement] = useState("");
  const announce = useCallback<FunnelAnnouncer>((message) => setAnnouncement(message), []);

  return (
    <LazyMotion features={domAnimation} strict>
      <FunnelAnnouncerContext.Provider value={announce}>
        <div
          ref={ref}
          data-brand={brand.id}
          data-testid={testId}
          className={`funnel-shell relative flex h-full w-full flex-col overflow-hidden ${className ?? ""}`}
          style={{
            ...buildBrandStyle(brand),
            background: "hsl(var(--brand-bg))",
            color: "hsl(var(--brand-bg-foreground))",
            fontFamily: "var(--brand-font-body)",
          }}
        >
          {(characterBackgroundUrl || characterBackgroundColor) && (
            <CharacterBackground
              url={characterBackgroundUrl}
              colorSeed={characterBackgroundColor}
            />
          )}
          <div
            className="relative z-10 flex h-full flex-1 flex-col"
            style={{
              paddingTop: "env(safe-area-inset-top)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            {header && (
              <header
                role="banner"
                className="sticky top-0 z-20"
                style={{
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  background: "hsl(var(--brand-bg) / 0.78)",
                }}
              >
                {header}
              </header>
            )}
            <main id="step-main" className="flex flex-1 flex-col overflow-y-auto px-5 pt-5 pb-6">
              {children}
            </main>
            {footer && (
              <footer
                role="contentinfo"
                className="sticky bottom-0 z-20 px-5 pt-3"
                style={{
                  paddingBottom: "max(env(safe-area-inset-bottom), 16px)",
                  background:
                    "linear-gradient(180deg, hsl(var(--brand-bg) / 0) 0%, hsl(var(--brand-bg)) 30%)",
                }}
              >
                {footer}
              </footer>
            )}
          </div>

          <div aria-live="polite" id="funnel-announcer" className="sr-only">
            {announcement}
          </div>
        </div>
      </FunnelAnnouncerContext.Provider>
    </LazyMotion>
  );
});

export default FunnelShell;
